import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { TextField, SelectField } from "@/components/common";
import { ACQUISITION_STRATEGY, usd } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator, Save, Wrench, Sparkles, Copy, RefreshCw } from "lucide-react";

const REPAIR_ITEMS = [
  ["Roof", 12000], ["Kitchen", 15000], ["Bathrooms", 8000], ["Flooring", 7000],
  ["Paint (Int/Ext)", 6000], ["HVAC", 7000], ["Plumbing", 5000], ["Electrical", 4500],
  ["Windows", 6000], ["Landscaping", 3000],
];

export default function DealAnalyzer() {
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState("");
  const [strategy, setStrategy] = useState("Cash Purchase");
  const [i, setI] = useState({ target_purchase_percentage: 0.70, monthly_vacancy: 0, reserves: 0 });
  const [repairs, setRepairs] = useState({});
  const [result, setResult] = useState(null);
  const [, setCompany] = useState({});

  const set = (k) => (v) => setI((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/leads").then((r) => setLeads(r.data));
    api.get("/company").then((r) => { setCompany(r.data); if (r.data.target_purchase_percentage) setI((p) => ({ ...p, target_purchase_percentage: r.data.target_purchase_percentage / 100 })); });
  }, []);

  const repairTotal = Object.entries(repairs).reduce((s, [k, on]) => s + (on ? (REPAIR_ITEMS.find((r) => r[0] === k)?.[1] || 0) : 0), 0);

  const onSelectLead = (id) => {
    setLeadId(id);
    const l = leads.find((x) => x.id === id);
    if (l) setI((p) => ({ ...p, purchase_price: l.asking_price || 0, estimated_market_value: l.property?.estimated_market_value || 0, arv: l.property?.estimated_market_value || 0, expected_rent: l.property?.estimated_rent || 0 }));
  };

  const [sp] = useSearchParams();
  useEffect(() => {
    const lid = sp.get("lead_id");
    if (lid && leads.length && !leadId) onSelectLead(lid);
  }, [leads]); // eslint-disable-line

  const compute = useCallback(async () => {
    const payload = { ...i, strategy, repair_costs: (Number(i.repair_costs) || 0) + repairTotal };
    const { data } = await api.post("/calculate", payload);
    setResult(data);
  }, [i, strategy, repairTotal]);

  const save = async () => {
    if (!leadId) return toast.error("Select a lead first");
    if (!result) await compute();
    const l = leads.find((x) => x.id === leadId);
    try {
      await api.post("/analyses", { lead_id: leadId, property_id: l?.property_id, strategy, ...i, repair_costs: (Number(i.repair_costs) || 0) + repairTotal, ...result, analysis_date: new Date().toISOString() });
      toast.success("Analysis saved to lead");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save analysis");
    }
  };

  useEffect(() => { compute(); }, [compute]);

  const results = result ? [
    { label: "Max Allowable Offer", value: usd(result.maximum_allowable_offer), hl: true },
    { label: "Estimated Equity", value: usd(result.estimated_equity) },
    { label: "Flip Net Profit", value: usd(result.flip_net_profit) },
    { label: "Cash Required", value: usd(result.cash_required) },
    { label: "Monthly Cash Flow", value: usd(result.monthly_cash_flow) },
    { label: "Cash-on-Cash", value: `${result.cash_on_cash_return}%` },
    { label: "DSCR", value: result.dscr },
    { label: "ROI", value: `${result.roi}%` },
  ] : [];

  return (
    <div>
      <PageHeader title="Deal Analyzer" subtitle="Cash · Subject-To · Seller Financing · Fix & Flip"
        action={<Button onClick={save} data-testid="save-analysis-btn"><Save className="w-4 h-4 mr-1" /> Save Analysis</Button>} />
      <div className="p-4 sm:p-6 lg:p-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card border border-border rounded-md p-5 grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium">Lead</div>
              <Select value={leadId} onValueChange={onSelectLead}>
                <SelectTrigger data-testid="analyzer-lead-select"><SelectValue placeholder="Select a lead (optional)" /></SelectTrigger>
                <SelectContent>{leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.lead_id_display} · {l.property?.property_address}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <SelectField label="Strategy" value={strategy} onChange={setStrategy} options={ACQUISITION_STRATEGY} testid="analyzer-strategy" />
          </div>

          <div className="bg-card border border-border rounded-md p-5">
            <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Calculator className="w-4 h-4 text-accent" /> Deal Inputs</h3>
            <div className="grid sm:grid-cols-3 gap-4">
              <TextField label="Purchase Price" type="number" value={i.purchase_price} onChange={set("purchase_price")} testid="in-purchase" />
              <TextField label="ARV" type="number" value={i.arv} onChange={set("arv")} testid="in-arv" />
              <TextField label="Est. Market Value" type="number" value={i.estimated_market_value} onChange={set("estimated_market_value")} testid="in-mv" />
              <TextField label="Existing Loan Balance" type="number" value={i.existing_loan_balance} onChange={set("existing_loan_balance")} testid="in-loan" />
              <TextField label="Known Liens" type="number" value={i.known_liens} onChange={set("known_liens")} testid="in-liens" />
              <TextField label="Arrearages" type="number" value={i.arrearages} onChange={set("arrearages")} testid="in-arrears" />
              <TextField label="Seller Consideration" type="number" value={i.seller_consideration} onChange={set("seller_consideration")} testid="in-seller" />
              <TextField label="Down Payment" type="number" value={i.down_payment} onChange={set("down_payment")} testid="in-down" />
              <TextField label="Reserves" type="number" value={i.reserves} onChange={set("reserves")} testid="in-reserves" />
              <TextField label="Repair Costs (manual)" type="number" value={i.repair_costs} onChange={set("repair_costs")} testid="in-repairs" />
              <TextField label="Closing Costs" type="number" value={i.closing_costs} onChange={set("closing_costs")} testid="in-closing" />
              <TextField label="Holding Costs" type="number" value={i.holding_costs} onChange={set("holding_costs")} testid="in-holding" />
              <TextField label="Selling Costs" type="number" value={i.selling_costs} onChange={set("selling_costs")} testid="in-selling" />
              <TextField label="Acquisition Fee" type="number" value={i.acquisition_fee} onChange={set("acquisition_fee")} testid="in-fee" />
              <TextField label="Target Purchase %" type="number" value={Math.round((i.target_purchase_percentage || 0) * 100)} onChange={(v) => set("target_purchase_percentage")((Number(v) || 0) / 100)} testid="in-target" />
            </div>
            <h4 className="font-display font-bold mt-6 mb-3 text-sm">Rental / Cash Flow</h4>
            <div className="grid sm:grid-cols-3 gap-4">
              <TextField label="Expected Rent" type="number" value={i.expected_rent} onChange={set("expected_rent")} testid="in-rent" />
              <TextField label="Monthly Debt Service" type="number" value={i.monthly_debt_service} onChange={set("monthly_debt_service")} testid="in-debt" />
              <TextField label="Monthly Taxes" type="number" value={i.monthly_taxes} onChange={set("monthly_taxes")} testid="in-taxes" />
              <TextField label="Monthly Insurance" type="number" value={i.monthly_insurance} onChange={set("monthly_insurance")} testid="in-ins" />
              <TextField label="Monthly HOA" type="number" value={i.monthly_hoa} onChange={set("monthly_hoa")} testid="in-hoa" />
              <TextField label="Monthly Mgmt" type="number" value={i.monthly_management} onChange={set("monthly_management")} testid="in-mgmt" />
              <TextField label="Monthly Maintenance" type="number" value={i.monthly_maintenance} onChange={set("monthly_maintenance")} testid="in-maint" />
              <TextField label="Monthly Vacancy" type="number" value={i.monthly_vacancy} onChange={set("monthly_vacancy")} testid="in-vac" />
            </div>
          </div>

          <div className="bg-card border border-border rounded-md p-5">
            <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Wrench className="w-4 h-4 text-accent" /> Repair Estimator</h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {REPAIR_ITEMS.map(([name, cost]) => (
                <label key={name} className="flex items-center justify-between px-3 py-2 rounded-md border border-border cursor-pointer hover:bg-secondary transition-colors" data-testid={`repair-${name.toLowerCase().replace(/[^a-z]/g, "")}`}>
                  <span className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!repairs[name]} onChange={(e) => setRepairs((p) => ({ ...p, [name]: e.target.checked }))} /> {name}</span>
                  <span className="font-mono-nums text-sm text-muted-foreground">{usd(cost)}</span>
                </label>
              ))}
            </div>
            <div className="mt-3 text-right font-display font-bold">Estimated Repairs: <span className="text-accent font-mono-nums">{usd(repairTotal)}</span></div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="sticky top-24 space-y-4">
            <AiDealStrategyPanel lead={leads.find((x) => x.id === leadId)} strategy={strategy} inputs={i} result={result} repairTotal={repairTotal} />
            {results.map((r) => (
              <div key={r.label} className={`rounded-md p-5 border ${r.hl ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border"}`} data-testid={`result-${r.label.toLowerCase().replace(/[^a-z]/g, "")}`}>
                <div className={`text-[11px] uppercase tracking-[0.18em] font-medium ${r.hl ? "text-accent" : "text-muted-foreground"}`}>{r.label}</div>
                <div className="font-display text-2xl font-bold font-mono-nums mt-1">{r.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const CONCLUSION_STYLE = {
  "Pursue": "bg-emerald-500/10 border-emerald-500/40 text-emerald-600 dark:text-emerald-400",
  "Review Carefully": "bg-amber-500/10 border-amber-500/40 text-amber-600 dark:text-amber-500",
  "Pass": "bg-destructive/10 border-destructive/40 text-destructive",
};

function AiDealStrategyPanel({ lead, strategy, inputs, result, repairTotal }) {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null); // {output, generated_at}
  const [busy, setBusy] = useState(false);
  const [sig, setSig] = useState("");

  const metrics = {
    purchase_price: num(inputs.purchase_price), arv: num(inputs.arv),
    estimated_market_value: num(inputs.estimated_market_value),
    repair_costs: (Number(inputs.repair_costs) || 0) + (repairTotal || 0) || undefined,
    existing_loan_balance: num(inputs.existing_loan_balance), expected_rent: num(inputs.expected_rent),
    monthly_debt_service: num(inputs.monthly_debt_service),
    maximum_allowable_offer: num(result?.maximum_allowable_offer), estimated_equity: num(result?.estimated_equity),
    flip_net_profit: num(result?.flip_net_profit), cash_required: num(result?.cash_required),
    monthly_cash_flow: num(result?.monthly_cash_flow), cash_on_cash_return: num(result?.cash_on_cash_return),
    dscr: num(result?.dscr), roi: num(result?.roi),
  };
  const currentSig = JSON.stringify({ strategy, metrics });

  useEffect(() => { api.get("/ai/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false })); }, []);
  useEffect(() => {
    setData(null); setSig("");
    if (lead?.id && status?.enabled) {
      api.get(`/ai/outputs/${lead.id}`).then((r) => {
        const ds = r.data.outputs?.deal_strategy;
        if (ds) { setData({ output: ds.output, generated_at: ds.generated_at }); setSig(currentSig); }
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [lead?.id, status?.enabled]);

  if (status && !status.enabled) {
    return <div className="bg-card border border-border rounded-md p-4 text-xs text-muted-foreground" data-testid="ai-deal-strategy-disabled">AI features are not enabled in this release.</div>;
  }
  if (!status) return null;

  const stale = !!data && sig && sig !== currentSig;
  const remaining = status.remaining ?? 0;
  const capped = remaining <= 0;

  const generate = async (force = false) => {
    if (!lead?.id) return toast.error("Select a lead first");
    setBusy(true);
    try {
      const { data: resp } = await api.post("/ai/deal-strategy", { lead_id: lead.id, strategy, metrics, force });
      setData({ output: resp.output, generated_at: resp.generated_at });
      setSig(currentSig);
      setStatus((s) => ({ ...s, remaining: resp.remaining, used: resp.used }));
      toast.success(resp.cached ? "Loaded existing result (no AI charge)" : "Deal strategy ready");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || "Could not generate the strategy");
    } finally { setBusy(false); }
  };

  const o = data?.output;
  return (
    <div className="bg-card border border-border rounded-md p-5" data-testid="ai-deal-strategy-card">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-display font-bold text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-accent" /> AI Deal Strategy
          {stale && <span className="text-[10px] uppercase tracking-wider bg-amber-500/10 text-amber-600 border border-amber-500/30 rounded-full px-2 py-0.5" data-testid="deal-strategy-stale">Stale</span>}</span>
        <Button size="sm" onClick={() => generate(!!o)} disabled={busy || capped || !lead?.id} data-testid="deal-strategy-generate-btn">
          {busy ? "Analyzing…" : o ? <><RefreshCw className="w-3.5 h-3.5 mr-1" />Regenerate</> : "Generate strategy"}
        </Button>
      </div>
      <div className="text-[11px] text-muted-foreground mb-3" data-testid="deal-strategy-counter">{remaining} of {status.limit} generations left · only calculated figures are sent (no seller PII)</div>

      {!o && <p className="text-xs text-muted-foreground" data-testid="deal-strategy-empty">Get an underwriter's read on this deal — a Pursue / Review / Pass call with rationale, risks, negotiation position and due-diligence items. Select a lead, then generate.</p>}

      {o && (
        <div className="space-y-3 text-sm" data-testid="deal-strategy-result">
          <div className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-display font-bold uppercase tracking-wider ${CONCLUSION_STYLE[o.verdict] || CONCLUSION_STYLE["Review Carefully"]}`} data-testid="deal-strategy-verdict">{o.verdict}</div>
          <Section t="Recommended Strategy">{o.recommended_strategy}</Section>
          <Section t="Rationale">{o.rationale}</Section>
          <Section t="Negotiation Position">{o.negotiation_position}</Section>
          {o.opportunities?.length > 0 && <Section t="Opportunities"><ul className="list-disc pl-4 space-y-1">{o.opportunities.map((x, n) => <li key={`${n}-${x}`}>{x}</li>)}</ul></Section>}
          {o.risks?.length > 0 && <Section t="Risks"><ul className="list-disc pl-4 space-y-1">{o.risks.map((x, n) => <li key={`${n}-${x}`}>{x}</li>)}</ul></Section>}
          {o.seller_questions?.length > 0 && <Section t="Seller Questions"><ul className="list-disc pl-4 space-y-1">{o.seller_questions.map((x, n) => <li key={`${n}-${x}`}>{x}</li>)}</ul></Section>}
          {o.due_diligence_items?.length > 0 && <Section t="Due Diligence"><ul className="list-disc pl-4 space-y-1">{o.due_diligence_items.map((x, n) => <li key={`${n}-${x}`}>{x}</li>)}</ul></Section>}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" onClick={copyStrategy(o)} data-testid="deal-strategy-copy"><Copy className="w-3.5 h-3.5 mr-1" /> Copy</Button>
            <Button size="sm" variant="outline" onClick={() => generate(true)} disabled={busy || capped} data-testid="deal-strategy-regenerate"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Regenerate</Button>
          </div>
          {data.generated_at && <div className="text-[11px] text-muted-foreground">Generated {new Date(data.generated_at).toLocaleString()}</div>}
        </div>
      )}
    </div>
  );
}

const num = (v) => (v === 0 || v ? Number(v) : undefined);

const copyStrategy = (o) => async () => {
  const text = [`Verdict: ${o.verdict}`, `Recommended Strategy: ${o.recommended_strategy}`,
    `Rationale: ${o.rationale}`, `Negotiation Position: ${o.negotiation_position}`,
    `Opportunities:\n- ${(o.opportunities || []).join("\n- ")}`,
    `Risks:\n- ${(o.risks || []).join("\n- ")}`,
    `Seller Questions:\n- ${(o.seller_questions || []).join("\n- ")}`,
    `Due Diligence:\n- ${(o.due_diligence_items || []).join("\n- ")}`].join("\n\n");
  try { await navigator.clipboard.writeText(text); toast.success("Strategy copied"); }
  catch { toast.error("Couldn't copy — select the text manually."); }
};


function Section({ t, children }) {
  return <div><div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-medium">{t}</div><div className="mt-0.5 leading-relaxed">{children}</div></div>;
}
